import { useState, useEffect } from 'react';
import { Accordion } from '../../../common/SharedComponents';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { getLoanTypeShortName, onRowPrepared } from '../../../loans/common/LoanFunctions';
import { getPaidColumnHtml, onDaysSinceAppliedColumnHtml } from '../../common/ApplicationFunctions';
import { BfiDataGrid, Column, MasterDetail } from '../../../common/SharedComponents';
import { getFileStatusColumnHtml } from '../../common/ApplicationFunctions';
import { Tooltip } from 'devextreme-react/tooltip';
import { borrowerTooltipText, addressTooltipText } from '../../../common/SharedFunctions';
import PortfolioTable from '../../submitted/tables/PortfolioTable';
import { ApplActionsButton } from '../../common/ApplicationComponents';
import { onCellDblClickReview } from '../../common/ApplicationFunctions';
import { GridSettingType } from '@apply/constants';
import { LoanType } from '../../../loans/common/LoanConstants';
import { useSelector } from 'react-redux';

const SubmittedNonRentalsTable = ({ isLOC, gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = isLOC
        ? (criteria) => applicationApi.getGrid_LOC(criteria)
        : (criteria) => applicationApi.getGrid_NonRentals(criteria);

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.application);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const actionCellRender = (e) => {
        return (
            <ApplActionsButton
                data={e.data}
                isPropertyLevel={false}
            />
        );
    };

    const onHighlightCellRender = (e) => {
        if (!e.value) {
            return null;
        }
        return (
            <div>
                <span
                    className='reddot'
                    style={{ position: 'unset' }}
                ></span>
            </div>
        );
    };

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onIsPaidCellRender = (e) => {
        return getPaidColumnHtml(e.value);
    };

    const onFileStatusCellRender = (e) => {
        return getFileStatusColumnHtml(e.value);
    };

    const onBorrowerCellRender = (e) => {
        const targetId = `be_${e.data.id}`;
        const text = borrowerTooltipText(e.data.borrower);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.borrowerEntity}
                    {e.data.propertyCount > 1 && (
                        <span>
                            <b>&nbsp;[+{e.data.propertyCount}]</b>
                        </span>
                    )}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                    wrapperAttr={{ class: 'bfi-tooltip' }}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    const onCityCellRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyCity}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `as_${e.data.id}}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyState}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const renderPortfolioRecords = (e) => {
        if (!e.data.isPortfolio) {
            return null;
        }
        return (
            <PortfolioTable
                data={e.data.portfolio}
                isRental={false}
                isBridge={e.data.loanTypeId === LoanType.Bridge}
            />
        );
    };

    return (
        <Accordion
            title={isLOC ? 'LOC Submitted' : 'Submitted'}
            subTitle='BFI to review, ensure items are acceptable. Collect missing items. If have everything, order appraisal. If incomplete after 30 days or will not be approved, send notice.'
        >
            <div className='card'>
                <div className='card-section'>
                    <BfiDataGrid
                        gridType={
                            isLOC
                                ? GridSettingType.Application_SubmittedTable_LOC
                                : GridSettingType.Application_SubmittedTable_NonRental
                        }
                        defaultPageSize={15}
                        dataStore={dataStore}
                        applicationInsights={applicationInsights}
                        onRowPrepared={onRowPrepared}
                        onCellDblClick={onCellDblClickReview}
                    >
                        <Column
                            dataField='highlight'
                            caption=''
                            width={20}
                            cellRender={onHighlightCellRender}
                            alignment='center'
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                        />
                        <Column
                            dataField='borrowerEntity'
                            caption='Entity'
                            minWidth={120}
                            alignment='left'
                            cellRender={onBorrowerCellRender}
                        />
                        <Column
                            dataField='propertyCity'
                            caption='City'
                            width={100}
                            alignment='left'
                            cellRender={onCityCellRender}
                        />
                        <Column
                            dataField='propertyState'
                            caption='State'
                            width={60}
                            alignment='left'
                            cellRender={onStateCellRender}
                        />
                        <Column
                            dataField='bfiNumber'
                            caption='BFI #'
                            dataType='string'
                            width={120}
                        />
                        <Column
                            dataField='daysSinceApplication'
                            caption='Applied'
                            width={100}
                            cellRender={onDaysSinceAppliedColumnHtml}
                            alignment='center'
                        />
                        <Column
                            dataField={'loanTypeId'}
                            caption='Type'
                            width={80}
                            cellRender={onLoanTypeCellRender}
                            alignment='left'
                        />
                        <Column
                            dataField='isPaid'
                            caption='Paid'
                            width={70}
                            cellRender={onIsPaidCellRender}
                            alignment='center'
                            cssClass='border-left'
                        />
                        <Column
                            dataField='fileStatusBudget'
                            caption='Budget'
                            width={70}
                            cellRender={(e) => getFileStatusColumnHtml(e.value, e.data.loanTypeId === LoanType.Bridge)}
                            alignment='center'
                        />
                        <Column
                            dataField='fileStatusSpecSheet'
                            caption='Specs'
                            width={70}
                            cellRender={(e) => getFileStatusColumnHtml(e.value, e.data.loanTypeId === LoanType.Bridge)}
                            alignment='center'
                        />
                        <Column
                            dataField='fileStatusPlans'
                            caption='Plan'
                            width={70}
                            cellRender={(e) => getFileStatusColumnHtml(e.value, e.data.loanTypeId === LoanType.Bridge)}
                            alignment='center'
                        />
                        <Column
                            dataField='fileStatusSurvey'
                            caption='Survey'
                            width={60}
                            cellRender={onFileStatusCellRender}
                            alignment='center'
                        />
                        <Column
                            dataField='responsibleName'
                            caption='Responsible'
                            dataType='string'
                            width={100}
                        />
                        <Column
                            alignment='center'
                            cellRender={actionCellRender}
                            width={50}
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                            type='actions'
                            fixed={true}
                            fixedPosition={'right'}
                        />
                        <MasterDetail
                            enabled={true}
                            render={renderPortfolioRecords}
                        />
                    </BfiDataGrid>
                </div>
            </div>
        </Accordion>
    );
};

export default SubmittedNonRentalsTable;
